import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, of, switchMap, tap } from 'rxjs';
import {
  PRIORITY_DIALOG_ACTIONS,
  SUBMISSION_DIALOG_ACTIONS,
  SUBMISSION_HEADER_ACTIONS,
  SUBMISSION_REVIEW_ACTIONS,
  VIEW_MORE_DATE_ACTIONS,
} from '../actions/submissions.actions';
import { SubmissionsService } from '../services';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ViewMoreDatesDialogComponent } from '@submissions/v1/header/view-more-dates-dialog/view-more-dates-dialog.component';
import { PriorityDialogComponent } from '@submissions/v1/header/priority-dialog/priority-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class SubmissionDataAccessEffects {
  private readonly _actions$ = inject(Actions); 
  private readonly _submissionsService = inject(SubmissionsService);  
  private readonly _matDialog = inject(MatDialog);  
  private readonly _store = inject(Store);  
  private readonly _matSnackBar = inject(MatSnackBar);  
  
  fetchViewMoreDates$ = createEffect(() =>
    this._actions$.pipe(
      ofType(VIEW_MORE_DATE_ACTIONS.fetchViewMoreDates),
      switchMap((action) =>
        this._submissionsService.viewMoreDatesReview(action.submissionId).pipe(
          switchMap((response) => {
            return [
              VIEW_MORE_DATE_ACTIONS.onfetchViewMoreDatesSuccess({
                payload: response,
              }),
            ];
          }),
          catchError((error) =>
            of(VIEW_MORE_DATE_ACTIONS.onfetchViewMoreDatesFailure({ error })),
          ),
        ),
      ),
    ),
  );

  fetchViewMoreDatesSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(VIEW_MORE_DATE_ACTIONS.onfetchViewMoreDatesSuccess),
        tap(({ payload }) => {
          if (payload.data) {
            this._store.dispatch(
              SUBMISSION_DIALOG_ACTIONS.openSubmissionDialog({
                component: () => ViewMoreDatesDialogComponent,
                data: {
                  title: 'Review Date',
                  data: [...payload.data],
                },
              }),
            );
          }
        }),
      ),
    { dispatch: false },
  );

  updateIRBPlus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SUBMISSION_HEADER_ACTIONS.updateIRBPlus),
      switchMap(({ irbState, isAPI }) => {
        return [
          SUBMISSION_HEADER_ACTIONS.updateIRBPlusSuccess({ irbState, isAPI }),
        ];
      }),
    ),
  );

  openPriorityDialog$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(PRIORITY_DIALOG_ACTIONS.openPriorityDialog),
        tap(({ title, description, priority, workspaceId }) => {
          this._store.dispatch(
            SUBMISSION_DIALOG_ACTIONS.openSubmissionDialog({
              component: () => PriorityDialogComponent,
              data: {
                title,
                description,
                priority,
                workspaceId,
              },
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  updateIRBPlusSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SUBMISSION_HEADER_ACTIONS.updateIRBPlusSuccess),
        tap(({ isAPI }) => {
          if (isAPI) {
            this._snackBarMessage('Priority saved.');
          }
        }),
      ),
    { dispatch: false },
  );

  /**
   * @Dialog Actions
   */

  openSubmissionDialog$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SUBMISSION_DIALOG_ACTIONS.openSubmissionDialog),
        tap(({ component, data }) => {
          this._matDialog.open(component(), {
            data,
          });
        }),
      ),
    { dispatch: false },
  );

  openSubmissionReviewDialog$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SUBMISSION_REVIEW_ACTIONS.openSubmissionReviewDialog),
        tap(({ component, data }) => {
          this._matDialog.open(component(), {
            data,
          });
        }),
      ),
    { dispatch: false },
  );

  /**
   * @Snack Bar Message
   */

  private _snackBarMessage(message: string, action = '') {
    this._matSnackBar.open(message, action, {
      duration: 4000,
    });
  }

}
